// useMasivaOptimizacion — P3-9b (remate de presentación). Toda la lógica del trabajo
// de optimización MASIVA (sondeo del estado, pedir confirmación con los conteos,
// iniciar/pausar/reanudar/cerrar), para que el DISPARADOR viva en un botón del
// encabezado y el PANEL grande aparezca SOLO mientras hay un trabajo en curso,
// pausado, en confirmación, o recién terminado (hasta que se cierra). El trabajo vive
// en el backend; el hook solo lo sondea, así que sobrevive a cerrar el navegador.
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { masivaEstado, masivaCandidatas, masivaIniciar, masivaPausarReanudar } from './productApi';

export default function useMasivaOptimizacion({ filtroIds = null, hasFilters = false, onChange, habilitado = true } = {}) {
  const [trabajo, setTrabajo] = useState(null);
  const [confirmando, setConfirmando] = useState(null); // { productos, fotos }
  const [ocupado, setOcupado] = useState(false);
  const [cerrado, setCerrado] = useState(false);        // panel «terminado» descartado
  const montado = useRef(true);
  const vistoActivo = useRef(false);                     // ¿vimos el trabajo corriendo en esta sesión?
  useEffect(() => () => { montado.current = false; }, []);

  const refrescar = useCallback(async () => {
    if (!habilitado) return null;
    const { trabajo: t } = await masivaEstado();
    if (montado.current) setTrabajo(t);
    return t;
  }, [habilitado]);

  // Sondeo: cada 3 s mientras hay un trabajo activo (corriendo/pausado).
  useEffect(() => {
    if (!habilitado) return undefined;
    refrescar();
    const id = setInterval(() => {
      if (trabajo && trabajo.estado !== 'terminado') refrescar();
    }, 3000);
    return () => clearInterval(id);
  }, [refrescar, trabajo, habilitado]);

  const activo = !!(trabajo && trabajo.estado !== 'terminado');
  useEffect(() => { if (activo) vistoActivo.current = true; }, [activo]);

  const alcance = hasFilters ? 'filtro' : 'catalogo';
  const ids = useMemo(() => (hasFilters ? (filtroIds || []) : []), [hasFilters, filtroIds]);

  const pedirConfirmacion = useCallback(async () => {
    setOcupado(true);
    try { setConfirmando(await masivaCandidatas(alcance, ids)); }
    finally { if (montado.current) setOcupado(false); }
  }, [alcance, ids]);

  const cancelarConfirmacion = useCallback(() => setConfirmando(null), []);

  const iniciar = useCallback(async () => {
    setOcupado(true);
    try {
      await masivaIniciar(alcance, ids);
      setConfirmando(null); setCerrado(false);
      await refrescar();
      if (onChange) onChange();
    } catch (e) { setConfirmando(null); alert(e.message); } // eslint-disable-line no-alert
    finally { if (montado.current) setOcupado(false); }
  }, [alcance, ids, refrescar, onChange]);

  const pausarReanudar = useCallback(async (accion) => {
    setOcupado(true);
    try { await masivaPausarReanudar(accion); await refrescar(); }
    finally { if (montado.current) setOcupado(false); }
  }, [refrescar]);

  const cerrar = useCallback(() => setCerrado(true), []);

  // El panel «terminado» solo se muestra si vimos el trabajo correr en esta sesión
  // (no un trabajo viejo ya terminado al cargar la página) y no se cerró.
  const terminadoVisible = !!(trabajo && trabajo.estado === 'terminado' && vistoActivo.current && !cerrado);
  const visiblePanel = activo || terminadoVisible || !!confirmando;

  return {
    trabajo, confirmando, ocupado, activo, terminadoVisible, visiblePanel,
    pedirConfirmacion, cancelarConfirmacion, iniciar, pausarReanudar, cerrar,
  };
}
